<template>
  <Teleport to="body">
    <WizardModal
      v-model="currentStep"
      :show-modal="isVisible"
      :modal-title="t('Novo Recebimento')"
      :steps="wizardSteps"
      size="medium"
      @finish="handleFinish"
      @cancel="handleCancel"
      @close="handleClose">

    <!-- Step 0: Forma de Pagamento -->
    <template #step-0>
      <div class="step-content">
        <h3>{{ t('Selecione a forma de pagamento') }}</h3>
        <buttons-list
          data-testid="recebimento-wizard-forma-pagamento"
          :placeholder="t('Escolha a forma de pagamento')"
          :validation="() => isFormaPagamentoValid"
          :required="true"
          elem-id="wizard_recebimento_forma_pagamento"
          v-model="formData.recebimento_forma_pagamento_id"
          :itens-select="formasPagamentoOptions">
        </buttons-list>
      </div>
    </template>

    <!-- Step 1: Data do Recebimento -->
    <template #step-1>
      <div class="step-content">
        <h3>{{ t('Informe a data do recebimento') }}</h3>
        <input-date
          data-testid="recebimento-wizard-data"
          :placeholder="t('Escolha a data')"
          :validation="() => isDataRecebimentoValid"
          :required="true"
          elem-id="wizard_recebimento_data"
          v-model="formData.recebimento_data">
        </input-date>
      </div>
    </template>

    <!-- Step 2: Valor -->
    <template #step-2>
      <div class="step-content">
        <h3>{{ t('Informe os valores do recebimento') }}</h3>
        <div class="valor-fields">
          <div class="valor-field">
            <input-currency
              ref="valorInputRef"
              data-testid="recebimento-wizard-valor"
              :label="t('Valor')"
              :placeholder="t('0,00')"
              :validation="() => isValorValid"
              :required="true"
              elem-id="wizard_recebimento_valor"
              v-model="valorDisplay">
            </input-currency>
            <small class="field-hint">{{ t('Valor pago pelo cliente') }}</small>
          </div>
          <div class="valor-field">
            <input-currency
              data-testid="recebimento-wizard-valor-taxas"
              :label="t('Taxas')"
              :placeholder="t('0,00')"
              :validation="() => true"
              :required="false"
              elem-id="wizard_recebimento_valor_taxas"
              v-model="valorTaxasDisplay">
            </input-currency>
            <small class="field-hint">{{ t('Taxas do processador de pagamento') }}</small>
          </div>
        </div>
        <div class="valor-liquido-display">
          <label>{{ t('Valor líquido') }}:</label>
          <span class="valor-liquido-value">{{ formatCurrency(valorLiquidoDisplay) }}</span>
          <small class="field-hint">{{ t('Valor menos taxas') }}</small>
        </div>
        <!-- Venda parcelada (chamado 2026-0067) -->
        <recebimento-parcelas-select
          v-model="formData.recebimento_numero_parcelas"
          elem-id="wizard_recebimento_numero_parcelas"
          testid="recebimento-wizard-parcelas"
          :max-parcelas="maxParcelas"
          :valor-centavos="formData.recebimento_valor || 0">
        </recebimento-parcelas-select>
      </div>
    </template>

    <!-- Step 3: Observações -->
    <template #step-3>
      <div class="step-content">
        <h3>{{ t('Observações (opcional)') }}</h3>
        <input-rich-text
          data-testid="recebimento-wizard-observacoes"
          :placeholder="t('Digite as observações')"
          :required="false"
          elem-id="wizard_recebimento_observacoes"
          v-model="formData.recebimento_observacoes">
        </input-rich-text>
      </div>
    </template>
    </WizardModal>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormasPagamento } from '@/composables/useFormasPagamento';
import { useRecebimento } from '@/composables/useRecebimento';
import { mensagemDaRecusaDeParcelamento, parcelasMaxDaForma } from '@/composables/useParcelamento';
import type { FormasPagamento_FormaPagamentoRow } from '@/contracts/FormasPagamento_FormaPagamentoRow';
import RecebimentoParcelasSelect from './RecebimentoParcelasSelect.vue';
import { permissionsService, permissionsState } from '@/services/permissionsService';
import { autoriaDoUsuarioLogado } from '../../utils/recebimentoAutoria';
import { LOCALE_TO_CURRENCY } from '@/utils/currencyMinorUnits';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface RecebimentoFormData {
  recebimento_id: number;
  recebimento_forma_pagamento_id: number | null;
  recebimento_valor: number;
  recebimento_valor_taxas: number;
  recebimento_valor_liquido: number;
  recebimento_observacoes: string;
  recebimento_franquia_id: number;
  recebimento_origem: string;
  recebimento_origem_id: number;
  recebimento_registrado_por: string;
  recebimento_registrado_por_id: string;
  recebimento_data_cadastro: string;
  recebimento_data: string;
  recebimento_numero_parcelas: number;
  [key: string]: unknown;
}

interface SelectOption {
  ID: Id;
  Name: string;
}


interface OpenPayload {
  compromissoId?: number;
  franquiaId?: number;
}

interface CompromissoData {
  compromissoId: number;
  franquiaId: number;
}

interface CompromissoApi {
  compromisso?: Record<string, unknown>;
  compromisso_forma_pagamento?: number;
  compromisso_numero_parcelas?: number | string;
  compromisso_total?: number | string;
  compromisso_valor_total?: number | string;
  compromisso_valor_recebido?: number;
  compromisso_data?: string;
  compromisso_data_hora_inicio?: string;
  data?: string;
  data_hora_inicio?: string;
  [key: string]: unknown;
}

interface CreateResponse {
  dados?: unknown;
  data?: unknown;
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean;
}

const { t } = useI18n();
const { listarFormasPagamento } = useFormasPagamento();
const { createRecebimento } = useRecebimento();

// No props needed - data will come from eventBus
const compromissoData = ref<CompromissoData>({
  compromissoId: 0,
  franquiaId: 0
});

// Wizard visibility
const isVisible = ref<boolean>(false);

// Wizard state
const currentStep = ref<number>(0);

// Reference to valor input for auto-focus
const valorInputRef = ref<{ $el?: HTMLElement } | null>(null);

// Form data
const formData = ref<RecebimentoFormData>({
  recebimento_id: 0,
  recebimento_forma_pagamento_id: null,
  recebimento_valor: 0, // gross value in centavos (what customer paid)
  recebimento_valor_taxas: 0, // fee value in centavos (payment processor fees)
  recebimento_valor_liquido: 0, // net value in centavos (valor - valor_taxas)
  recebimento_observacoes: '',
  recebimento_franquia_id: 0,
  recebimento_origem: 'compromisso',
  recebimento_origem_id: 0,
  ...autoriaDoUsuarioLogado(),
  recebimento_data_cadastro: new Date().toISOString(),
  recebimento_data: new Date().toISOString().slice(0, 10), // Y-m-d format
  recebimento_numero_parcelas: 1
});

// Options for formas de pagamento
const formasPagamento = ref<FormasPagamento_FormaPagamentoRow[]>([]);
const formasPagamentoOptions = ref<SelectOption[]>([]);

// Venda parcelada (chamado 2026-0067): quantas parcelas a forma escolhida aceita.
const maxParcelas = computed<number>(() => parcelasMaxDaForma(
  formasPagamento.value.find((fp) => Number(fp.forma_pagamento_id) === Number(formData.value.recebimento_forma_pagamento_id)) ?? null
));

// Price factor derived from active locale (100 for BRL, 1 for CLP, etc.)
const PRICE_FACTOR_BRL = 100;


// Currency handling - convert between minor units and display value
// Valor (gross - what customer paid)
const valorDisplay = computed<number>({
  get: () => {
    return (formData.value.recebimento_valor || 0) / PRICE_FACTOR_BRL;
  },
  set: (value: number) => {
    formData.value.recebimento_valor = Math.round(value * PRICE_FACTOR_BRL);
    calculateValorLiquido();
  }
});

// Valor taxas (fees from payment processor)
const valorTaxasDisplay = computed<number>({
  get: () => {
    return (formData.value.recebimento_valor_taxas || 0) / PRICE_FACTOR_BRL;
  },
  set: (value: number) => {
    formData.value.recebimento_valor_taxas = Math.round(value * PRICE_FACTOR_BRL);
    calculateValorLiquido();
  }
});

// Valor liquido (net - valor minus taxas) - read only
const valorLiquidoDisplay = computed<number>(() => {
  const valor = formData.value.recebimento_valor || 0;
  const taxas = formData.value.recebimento_valor_taxas || 0;
  return (valor - taxas) / PRICE_FACTOR_BRL;
});

// Calculate and update valor_liquido (net) when valor or taxas change
const calculateValorLiquido = (): void => {
  const valor = formData.value.recebimento_valor || 0;
  const taxas = formData.value.recebimento_valor_taxas || 0;
  formData.value.recebimento_valor_liquido = valor - taxas;
};

// Format currency for display using the active user locale
const formatCurrency = (value: number | undefined): string => {
  const locale = (permissionsState as { normalizedLocale?: string })?.normalizedLocale || 'pt-BR';
  const currency = LOCALE_TO_CURRENCY[locale] || 'BRL';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value || 0);
};

// Validation functions
const isFormaPagamentoValid = computed<boolean>(() => {
  return !!formData.value.recebimento_forma_pagamento_id &&
         formData.value.recebimento_forma_pagamento_id > 0;
});

const isValorValid = computed<boolean>(() => {
  // Gross value (valor) must be positive, net value (valor_liquido) can be 0 or positive
  return formData.value.recebimento_valor > 0;
});

const isDataRecebimentoValid = computed<boolean>(() => {
  const dateValue = formData.value.recebimento_data as string | Date | undefined;

  log('RecebimentoWizard', 'isDataRecebimentoValid', 'Validating date', {
    dateValue,
    typeOf: typeof dateValue
  });

  if (!dateValue) {
    log('RecebimentoWizard', 'isDataRecebimentoValid', 'No date value - invalid');
    return false;
  }

  // Handle both Date objects and string formats
  let dateStr: string = '';
  if (dateValue instanceof Date) {
    dateStr = dateValue.toISOString().slice(0, 10);
  } else if (typeof dateValue === 'string') {
    // Extract just the date part (handle datetime strings too)
    dateStr = dateValue.split('T')[0].split(' ')[0];
  }

  // Parse as local date by splitting the string (avoids UTC timezone issues)
  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    log('RecebimentoWizard', 'isDataRecebimentoValid', 'Invalid date format', { dateStr, parts });
    return false;
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
  const day = parseInt(parts[2], 10);

  // Create dates in local timezone for comparison
  const selectedDate = new Date(year, month, day);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const isValid = selectedDate <= today;

  log('RecebimentoWizard', 'isDataRecebimentoValid', 'Validation result', {
    dateStr,
    selectedDate: selectedDate.toISOString(),
    today: today.toISOString(),
    isValid
  });

  return isValid;
});

// Wizard steps configuration
const wizardSteps = ref<WizardStep[]>([
  {
    title: t('Forma de Pagamento'),
    description: t('Escolha como foi realizado o pagamento'),
    validate: () => isFormaPagamentoValid.value
  },
  {
    title: t('Data do Recebimento'),
    description: t('Informe a data em que o recebimento ocorreu'),
    validate: () => isDataRecebimentoValid.value
  },
  {
    title: t('Valor'),
    description: t('Informe o valor recebido'),
    validate: () => isValorValid.value
  },
  {
    title: t('Observações'),
    description: t('Adicione observações se necessário'),
    validate: () => true // Optional step, always valid
  }
]);

// Watch for step changes to handle focus
watch(currentStep, (newStep: number) => {
  // When moving to step 2 (Valor), focus the input
  if (newStep === 2) {
    nextTick(() => {
      const el = valorInputRef.value?.$el;
      if (el) {
        const input = el.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    });
  }
});

// Watch for date changes to debug
watch(() => formData.value.recebimento_data, (newValue, oldValue) => {
  log('RecebimentoWizard', 'watch:recebimento_data', 'Date value changed', {
    oldValue,
    newValue,
    typeOf: typeof newValue
  });
});

// Load formas de pagamento
const loadFormasPagamento = async (): Promise<void> => {
  try {
    log('RecebimentoWizard', 'loadFormasPagamento', 'Loading payment methods');
    const lista = await listarFormasPagamento(false, compromissoData.value.franquiaId) as FormasPagamento_FormaPagamentoRow[];
    formasPagamento.value = lista;

    formasPagamentoOptions.value = lista.map((fp: FormasPagamento_FormaPagamentoRow) => ({
      ID: fp.forma_pagamento_id,
      Name: fp.forma_pagamento_nome
    }));

    log('RecebimentoWizard', 'loadFormasPagamento', 'Loaded payment methods', formasPagamentoOptions.value.length);
  } catch (error) {
    log('RecebimentoWizard', 'loadFormasPagamento', 'Error loading payment methods', error);
  }
};

// Handle wizard finish
const handleFinish = async (): Promise<void> => {
  try {
    log('RecebimentoWizard', 'handleFinish', 'Creating recebimento', formData.value);

    const f = formData.value;
    const payload = {
      recebimento_id: f.recebimento_id,
      recebimento_forma_pagamento_id: f.recebimento_forma_pagamento_id ?? undefined,
      recebimento_valor: f.recebimento_valor,
      recebimento_valor_taxas: f.recebimento_valor_taxas,
      recebimento_valor_liquido: f.recebimento_valor_liquido,
      recebimento_observacoes: f.recebimento_observacoes,
      recebimento_franquia_id: f.recebimento_franquia_id,
      recebimento_origem: f.recebimento_origem,
      recebimento_origem_id: f.recebimento_origem_id,
      recebimento_registrado_por: f.recebimento_registrado_por,
      recebimento_registrado_por_id: f.recebimento_registrado_por_id,
      recebimento_data_cadastro: f.recebimento_data_cadastro,
      recebimento_data: f.recebimento_data,
      recebimento_numero_parcelas: f.recebimento_numero_parcelas > 1 ? f.recebimento_numero_parcelas : undefined,
    };
    const response = await createRecebimento(payload) as CreateResponse;

    log('RecebimentoWizard', 'handleFinish', 'Recebimento created successfully', response);

    // Emit global event for anyone listening
    eventBus.emit('recebimento.created', response.dados || response.data);

    handleClose();
  } catch (error) {
    log('RecebimentoWizard', 'handleFinish', 'Error creating recebimento', error);
    eventBus.emit('globalMessage.error', mensagemDaRecusaDeParcelamento(error, t) ?? t('Erro ao salvar recebimento'));
  }
};

// Handle wizard cancel/close
const handleCancel = (): void => {
  log('RecebimentoWizard', 'handleCancel', 'Wizard canceled');
  handleClose();
};

const handleClose = (): void => {
  log('RecebimentoWizard', 'handleClose', 'Closing wizard');
  isVisible.value = false;

  // Reset wizard state
  currentStep.value = 0;

  // Reset form data
  formData.value = {
    recebimento_id: 0,
    recebimento_forma_pagamento_id: null,
    recebimento_valor: 0,
    recebimento_valor_taxas: 0,
    recebimento_valor_liquido: 0,
    recebimento_observacoes: '',
    recebimento_franquia_id: 0,
    recebimento_origem: 'compromisso',
    recebimento_origem_id: 0,
    ...autoriaDoUsuarioLogado(),
    recebimento_data_cadastro: new Date().toISOString(),
    recebimento_data: new Date().toISOString().slice(0, 10), // Y-m-d format
    recebimento_numero_parcelas: 1
  };
};

// Open wizard via eventBus
const open = async (payload?: OpenPayload): Promise<void> => {
  log('RecebimentoWizard', 'open', 'Opening wizard via eventBus', payload);

  if (!payload) return;

  // Store compromisso data
  compromissoData.value = {
    compromissoId: payload.compromissoId || 0,
    franquiaId: payload.franquiaId || 0
  };

  // Update form data with received values
  formData.value.recebimento_origem_id = compromissoData.value.compromissoId;
  formData.value.recebimento_franquia_id = compromissoData.value.franquiaId;
  formData.value.recebimento_registrado_por_id = permissionsService.getUser()?.sub || '';
  formData.value.recebimento_data_cadastro = new Date().toISOString();
  formData.value.recebimento_data = new Date().toISOString().slice(0, 10); // Y-m-d format

  // Load formas de pagamento before opening
  await loadFormasPagamento();

  // Fetch compromisso details to get default payment method and remaining value
  if (compromissoData.value.compromissoId > 0) {
    try {
      const { useCompromisso } = await import('@/composables/useCompromisso');
      const { fetchCompromisso } = useCompromisso();
      const result = await fetchCompromisso(compromissoData.value.compromissoId) as CompromissoApi;

      log('RecebimentoWizard', 'open', 'Fetched compromisso result', result);

      // Extract compromisso from nested structure
      const compromisso = (result.compromisso || result) as CompromissoApi;
      log('RecebimentoWizard', 'open', 'Compromisso data', compromisso);

      // Set default payment method from compromisso
      if (compromisso.compromisso_forma_pagamento) {
        formData.value.recebimento_forma_pagamento_id = compromisso.compromisso_forma_pagamento;
        // As parcelas combinadas no compromisso vêm pré-escolhidas; o seletor as devolve a 1 se a
        // forma não as aceitar.
        formData.value.recebimento_numero_parcelas = Math.max(1, Number(compromisso.compromisso_numero_parcelas ?? 1) || 1);
        log('RecebimentoWizard', 'open', 'Set default payment method', compromisso.compromisso_forma_pagamento);
      }

      // Calculate remaining value (total - already received)
      const compromissoTotal = parseFloat(String(compromisso.compromisso_total ?? compromisso.compromisso_valor_total ?? 0));
      const valorRecebido = (compromisso.compromisso_valor_recebido || 0) / PRICE_FACTOR_BRL; // Convert minor units to display value
      const valorRestante = Math.max(0, compromissoTotal - valorRecebido);

      log('RecebimentoWizard', 'open', 'Valor calculation', {
        compromissoTotal,
        valorRecebidoCentavos: compromisso.compromisso_valor_recebido,
        valorRecebidoReais: valorRecebido,
        valorRestante
      });

      // Set default value as remaining value (convert reais to centavos)
      if (valorRestante > 0) {
        const valorCentavos = Math.round(valorRestante * PRICE_FACTOR_BRL);
        formData.value.recebimento_valor = valorCentavos;
        formData.value.recebimento_valor_taxas = 0;
        formData.value.recebimento_valor_liquido = valorCentavos;
        log('RecebimentoWizard', 'open', 'Set default value to remaining', {
          valorCentavos: formData.value.recebimento_valor,
          valorTaxasCentavos: formData.value.recebimento_valor_taxas,
          valorLiquidoCentavos: formData.value.recebimento_valor_liquido
        });
      }

      // Set recebimento_data to compromisso date
      const compromissoDate = compromisso.compromisso_data ||
                             compromisso.compromisso_data_hora_inicio ||
                             compromisso.data ||
                             compromisso.data_hora_inicio;

      if (compromissoDate) {
        // Extract Y-m-d format from date (handle both date and datetime formats)
        const dateOnly = compromissoDate.split(' ')[0]; // Get date part if datetime
        formData.value.recebimento_data = dateOnly;
        log('RecebimentoWizard', 'open', 'Set recebimento_data to compromisso date', {
          compromissoDate,
          recebimentoData: formData.value.recebimento_data
        });
      }
    } catch (error) {
      log('RecebimentoWizard', 'open', 'Error fetching compromisso details', error);
      // Continue opening wizard even if fetch fails
    }
  }

  isVisible.value = true;
};

// Listen for eventBus events
onMounted(() => {
  // Clean up any stale listeners first (handles HMR edge cases where
  // component is hot-reloaded without proper unmount)
  eventBus.removeAllListeners('recebimento-wizard.open');

  log('RecebimentoWizard', 'onMounted', 'Setting up eventBus listeners');
  eventBus.on('recebimento-wizard.open', open);

  // Log listener count for debugging
  log('RecebimentoWizard', 'onMounted', 'Listener count:', eventBus.getListenerCount('recebimento-wizard.open'));
});

onBeforeUnmount(() => {
  log('RecebimentoWizard', 'onBeforeUnmount', 'Cleaning up eventBus listeners');
  eventBus.off('recebimento-wizard.open', open);
});
</script>

<style scoped>
.step-content {
  padding: 20px;
}

.step-content h3 {
  margin-bottom: 20px;
  font-size: 1.25rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

/* Valor fields layout */
.valor-fields {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.valor-field {
  flex: 1;
  min-width: 150px;
}

/* Field hints */
.field-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}

/* Valor liquido display */
.valor-liquido-display {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.valor-liquido-display label {
  font-weight: 600;
  color: #495057;
  margin: 0;
}

.valor-liquido-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

.valor-liquido-display .field-hint {
  flex-basis: 100%;
  margin-top: 5px;
}

/* Responsive */
@media (max-width: 600px) {
  .valor-fields {
    flex-direction: column;
    gap: 15px;
  }

  .valor-field {
    min-width: 100%;
  }
}
</style>
